import { useEffect, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { api } from "@/lib/api";
import type { PqEvent, Service } from "@/lib/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDynoWs } from "@/hooks/useDynoWs";
import { useDynoMqtt, isMqttV2Enabled } from "@/hooks/useDynoMqtt";
import { useLiveFeed } from "@/stores/liveFeed";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface PqListResponse {
  items: PqEvent[];
  total: number;
  limit: number;
  offset: number;
}

const PQ_PAGE_SIZE = 100;

type PqSortKey = "trigger_ts" | "event_type" | "severity" | "peak_a"
  | "depth_pct" | "duration_ms";

const PQ_SORT_LABELS: Record<PqSortKey, string> = {
  trigger_ts: "Time",
  event_type: "Type",
  severity: "Severity",
  peak_a: "Peak RMS",
  depth_pct: "Depth / Mag %",
  duration_ms: "Duration",
};

function fmtNum(v: number | undefined | null, digits = 1): string {
  if (v == null) return "—";
  return v.toFixed(digits);
}

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const SIG_STYLES: Record<string, string> = {
  ok: "text-emerald-400",
  stale: "text-muted-foreground",
};

export function ServiceDetail() {
  const { systemId } = useParams<{ systemId: string }>();
  const [sp] = useSearchParams();
  const windowId = sp.get("window");
  const navigate = useNavigate();
  const location = useLocation();

  // Return to the dashboard. navigate(-1) pops history so the dashboard
  // restores its prior scroll position (see Dashboard's scroll-restore
  // effect); fall back to a direct route when this view was opened cold
  // (deep link / refresh) with no in-app history entry to pop.
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate(`/?window=${windowId ?? ""}`);
  };

  const [svc, setSvc] = useState<Service | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [events, setEvents] = useState<PqEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(0);  // 0-indexed
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsErr, setEventsErr] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<PqSortKey>("peak_a");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Reset to page 0 whenever the system, window, or sort changes — a fresh
  // load should never land on a stale offset for a different ordering.
  useEffect(() => {
    setEventsPage(0);
  }, [systemId, windowId, sortBy, sortOrder]);

  // Click a column header: same column toggles asc/desc, new column starts
  // at desc (the more useful default for severity/peak/time).
  const onSortClick = (key: PqSortKey) => {
    setSortBy((prev) => {
      if (prev === key) {
        setSortOrder((o) => (o === "desc" ? "asc" : "desc"));
        return prev;
      }
      setSortOrder("desc");
      return key;
    });
  };

  // Gate the realtime source by ?mqtt_v2 (mirror Dashboard). MQTT (default-on)
  // carries the live scalars AND the ovr/dyno/pq THD topic; WS is the
  // auto-fallback when MQTT trips its failure threshold. Only one subscribes
  // at a time. PQ scalars only arrive on the MQTT path (WS-fallback shows "—").
  const mqttV2Wanted = isMqttV2Enabled();
  const mqttHook = useDynoMqtt({ enabled: mqttV2Wanted });
  const useWsFallback = !mqttV2Wanted || mqttHook.mqttFailed;
  useDynoWs({ enabled: useWsFallback });
  const live = useLiveFeed((s) => (systemId ? s.services[systemId] : undefined));
  const storeConnected = useLiveFeed((s) => s.connected);
  const wsConnected = useWsFallback ? storeConnected : mqttHook.mqttConnected;

  // Scroll handling. Arriving forward (a tile tapped on the dashboard) opens
  // at the top — an SPA route change keeps the window scroll otherwise, so a
  // tile far down the page would open its detail mid-view. Arriving back via
  // POP (the event-detail Back button) restores the prior offset so the
  // operator lands on the PQ-events section they came from. Runs once per
  // system, after the service + its events render so the offset is reachable.
  const navType = useNavigationType();
  const scrolledFor = useRef<string | null>(null);
  useEffect(() => {
    if (!svc || eventsLoading) return;
    if (scrolledFor.current === systemId) return;
    scrolledFor.current = systemId ?? null;
    const saved = sessionStorage.getItem("dyno:serviceScrollY");
    if (navType === "POP" && saved != null) {
      sessionStorage.removeItem("dyno:serviceScrollY");
      const y = Number(saved);
      requestAnimationFrame(() =>
        window.scrollTo(0, Number.isFinite(y) ? y : 0),
      );
    } else {
      sessionStorage.removeItem("dyno:serviceScrollY");
      window.scrollTo(0, 0);
    }
  }, [svc, eventsLoading, navType, systemId]);

  useEffect(() => {
    if (!systemId || !windowId) return;
    let cancelled = false;
    api
      .get<{ services: Service[] }>(`/api/services?window_id=${encodeURIComponent(windowId)}`)
      .then((r) => {
        if (cancelled) return;
        const match = r.services.find((s) => s.system_id === systemId) ?? null;
        setSvc(match);
      })
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
  }, [systemId, windowId]);

  useEffect(() => {
    if (!systemId) return;
    let cancelled = false;
    setEventsLoading(true);
    setEventsErr(null);
    const params = new URLSearchParams();
    params.set("system_id", systemId);
    params.set("limit", String(PQ_PAGE_SIZE));
    params.set("offset", String(eventsPage * PQ_PAGE_SIZE));
    params.set("sort_by", sortBy);
    params.set("sort_order", sortOrder);
    if (windowId) params.set("window_id", windowId);
    api
      .get<PqListResponse>(`/api/pq/events?${params.toString()}`)
      .then((r) => {
        if (cancelled) return;
        setEvents(r.items);
        setEventsTotal(r.total ?? 0);
      })
      .catch((e) => !cancelled && setEventsErr(String(e)))
      .finally(() => !cancelled && setEventsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [systemId, windowId, eventsPage, sortBy, sortOrder]);

  if (error) return <div className="text-destructive text-sm">Error: {error}</div>;
  if (!svc) return <div className="text-muted-foreground text-sm">Loading…</div>;

  const sig = live?.sig ?? "ok";
  const va = live?.v?.[0];
  const vb = live?.v?.[1];
  const vc = live?.v?.[2];
  const ia = live?.i?.[0];
  const ib = live?.i?.[1];
  const ic = live?.i?.[2];

  return (
    <div className="space-y-6">
      <div>
        <button
          type="button"
          onClick={goBack}
          className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <span aria-hidden>←</span> Dashboard
        </button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {svc.service_name ?? svc.system_id}
        </h1>
        <p className="text-sm text-muted-foreground">
          <span className="font-mono">{svc.system_id}</span>
          {" · "}
          {svc.node_id ?? "— node —"}
          {" · "}
          {svc.location ?? "— location —"}
          {" · "}
          <span className={cn(wsConnected ? "text-emerald-400" : "text-muted-foreground")}>
            {wsConnected ? "● live" : "○ waiting"}
          </span>
          {live && (
            <>
              {" · "}
              <span className={cn("uppercase text-xs", SIG_STYLES[sig])}>{sig}</span>
            </>
          )}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Live scalars</CardTitle>
        </CardHeader>
        <CardContent>
          {!live ? (
            <div className="text-sm text-muted-foreground">— waiting for live data —</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Per-phase</div>
                <table className="w-full text-sm tabular-nums">
                  <thead>
                    <tr className="text-muted-foreground text-xs">
                      <th className="text-left font-normal"></th>
                      <th className="text-right font-normal pl-4">A</th>
                      <th className="text-right font-normal pl-4">B</th>
                      <th className="text-right font-normal pl-4">C</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-muted-foreground">Voltage (V)</td>
                      <td className="text-right font-medium pl-4">{fmtNum(va)}</td>
                      <td className="text-right font-medium pl-4">{fmtNum(vb)}</td>
                      <td className="text-right font-medium pl-4">{fmtNum(vc)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted-foreground">Current (A)</td>
                      <td className="text-right font-medium pl-4">{fmtNum(ia)}</td>
                      <td className="text-right font-medium pl-4">{fmtNum(ib)}</td>
                      <td className="text-right font-medium pl-4">{fmtNum(ic)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">System</div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm tabular-nums">
                  <dt className="text-muted-foreground">Power</dt>
                  <dd className="text-right font-medium">
                    {live.p != null ? `${(live.p / 1000).toFixed(2)} kW` : "—"}
                  </dd>
                  <dt className="text-muted-foreground">Power factor</dt>
                  <dd className="text-right font-medium">{fmtNum(live.pf, 2)}</dd>
                  <dt className="text-muted-foreground">Frequency</dt>
                  <dd className="text-right font-medium">{fmtNum(live.f, 2)} Hz</dd>
                </dl>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Power Quality
            <span className="text-xs font-normal text-muted-foreground ml-2">
              live · 3s aggregation
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!live || !(live.v_thd || live.i_thd || live.i_tdd || live.i_kfactor) ? (
            <div className="text-sm text-muted-foreground">— no live power-quality data —</div>
          ) : (
            <table className="w-full max-w-md text-sm tabular-nums">
              <thead>
                <tr className="text-muted-foreground text-xs">
                  <th className="text-left font-normal"></th>
                  <th className="text-right font-normal pl-4">A</th>
                  <th className="text-right font-normal pl-4">B</th>
                  <th className="text-right font-normal pl-4">C</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="text-muted-foreground">Voltage THD (%)</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.v_thd?.[0], 2)}</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.v_thd?.[1], 2)}</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.v_thd?.[2], 2)}</td>
                </tr>
                <tr>
                  <td className="text-muted-foreground">Current THD (%)</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.i_thd?.[0], 2)}</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.i_thd?.[1], 2)}</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.i_thd?.[2], 2)}</td>
                </tr>
                <tr>
                  <td className="text-muted-foreground">Current TDD (%)</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.i_tdd?.[0], 2)}</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.i_tdd?.[1], 2)}</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.i_tdd?.[2], 2)}</td>
                </tr>
                <tr>
                  <td className="text-muted-foreground">Current K-factor</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.i_kfactor?.[0], 2)}</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.i_kfactor?.[1], 2)}</td>
                  <td className="text-right font-medium pl-4">{fmtNum(live.i_kfactor?.[2], 2)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>
            PQ events
            <span className="text-xs font-normal text-muted-foreground ml-2">
              by {PQ_SORT_LABELS[sortBy].toLowerCase()}
              {" "}{sortOrder === "desc" ? "↓" : "↑"}
            </span>
            {!eventsLoading && eventsTotal > 0 && (
              <span className="text-muted-foreground text-sm font-normal ml-2">
                · {eventsPage * PQ_PAGE_SIZE + 1}
                –{Math.min((eventsPage + 1) * PQ_PAGE_SIZE, eventsTotal)}
                {" of "}{eventsTotal.toLocaleString()}
              </span>
            )}
          </CardTitle>
          {eventsTotal > PQ_PAGE_SIZE && (
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                className="px-2 py-1 rounded border border-border/60 disabled:opacity-40 hover:bg-muted/40"
                onClick={() => setEventsPage((p) => Math.max(0, p - 1))}
                disabled={eventsPage === 0 || eventsLoading}
              >
                ← Newer
              </button>
              <span className="text-muted-foreground tabular-nums">
                page {eventsPage + 1}
                {" / "}
                {Math.max(1, Math.ceil(eventsTotal / PQ_PAGE_SIZE))}
              </span>
              <button
                type="button"
                className="px-2 py-1 rounded border border-border/60 disabled:opacity-40 hover:bg-muted/40"
                onClick={() => setEventsPage((p) => p + 1)}
                disabled={
                  eventsLoading
                  || (eventsPage + 1) * PQ_PAGE_SIZE >= eventsTotal
                }
              >
                Older →
              </button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {eventsErr ? (
            <div className="text-sm text-destructive">{eventsErr}</div>
          ) : eventsLoading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : events.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No events captured for this system yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTH
                    sortKey="trigger_ts"
                    activeKey={sortBy}
                    order={sortOrder}
                    onClick={onSortClick}
                    className="w-40"
                  >
                    {PQ_SORT_LABELS.trigger_ts}
                  </SortableTH>
                  <SortableTH
                    sortKey="event_type"
                    activeKey={sortBy}
                    order={sortOrder}
                    onClick={onSortClick}
                  >
                    {PQ_SORT_LABELS.event_type}
                  </SortableTH>
                  {/* Decision has no useful sort — leave unsortable. */}
                  <TableHead>Decision</TableHead>
                  <SortableTH
                    sortKey="severity"
                    activeKey={sortBy}
                    order={sortOrder}
                    onClick={onSortClick}
                  >
                    {PQ_SORT_LABELS.severity}
                  </SortableTH>
                  <SortableTH
                    sortKey="peak_a"
                    activeKey={sortBy}
                    order={sortOrder}
                    onClick={onSortClick}
                    align="right"
                  >
                    {PQ_SORT_LABELS.peak_a}
                  </SortableTH>
                  <TableHead className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <SortLink
                        sortKey="depth_pct"
                        activeKey={sortBy}
                        order={sortOrder}
                        onClick={onSortClick}
                      >
                        {PQ_SORT_LABELS.depth_pct}
                      </SortLink>
                      <Popover>
                        <PopoverTrigger
                          type="button"
                          onClick={(ev) => ev.stopPropagation()}
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="What is Depth / Mag %?"
                        >
                          <span
                            aria-hidden
                            className="text-[10px] border border-current rounded-full w-3.5 h-3.5 inline-flex items-center justify-center leading-none"
                          >
                            ?
                          </span>
                        </PopoverTrigger>
                        <PopoverContent
                          align="end"
                          onClick={(ev) => ev.stopPropagation()}
                          className="text-xs space-y-2"
                        >
                          <div>
                            <span className="font-medium">Depth</span>
                            {" — "}
                            how far the voltage <em>dropped</em> below nominal
                            during a sag, as a percentage of nominal.
                            {" "}<span className="text-muted-foreground">
                              30% = voltage fell to 70% of nominal
                              (e.g. 84V on a 120V circuit).
                            </span>
                          </div>
                          <div>
                            <span className="font-medium">Mag %</span>
                            {" — "}
                            how far the voltage <em>rose</em> above nominal
                            during a swell, as a percentage of nominal.
                            {" "}<span className="text-muted-foreground">
                              15% = voltage rose to 115% of nominal
                              (e.g. 138V on a 120V circuit).
                            </span>
                          </div>
                          <div className="text-muted-foreground pt-1 border-t border-border/30">
                            A given event is either a sag (depth) or a swell
                            (mag), so usually only one column has a value.
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((e) => (
                  <TableRow
                    key={e.event_id}
                    className="cursor-pointer"
                    onClick={() => {
                      // Remember scroll so Back from the event detail lands
                      // the operator on this PQ-events section.
                      sessionStorage.setItem(
                        "dyno:serviceScrollY",
                        String(window.scrollY),
                      );
                      navigate(
                        `/events/${encodeURIComponent(e.event_id)}?window=${windowId ?? ""}`,
                      );
                    }}
                  >
                    <TableCell className="tabular-nums text-xs">{fmtTs(e.trigger_ts)}</TableCell>
                    <TableCell className="text-xs">{e.event_type ?? "—"}</TableCell>
                    <TableCell className="text-xs">{e.decision ?? "keep"}</TableCell>
                    <TableCell className="text-xs">{e.severity ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {e.peak_a != null
                        ? <>
                            {e.peak_a.toFixed(1)}A
                            {e.peak_phase && (
                              <span className="text-muted-foreground ml-1">
                                {e.peak_phase}
                              </span>
                            )}
                          </>
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-xs">
                      {e.depth_pct != null ? `${e.depth_pct.toFixed(1)}%` : ""}
                      {e.depth_pct != null && e.magnitude_pct != null && " / "}
                      {e.magnitude_pct != null ? `${e.magnitude_pct.toFixed(1)}%` : ""}
                      {e.depth_pct == null && e.magnitude_pct == null && "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Sortable header cell — wraps TableHead with a clickable button + arrow.
// Renders just the label and arrow; the active arrow is full-opacity, the
// inactive ones a faint hint so the column reads as sortable without
// clutter.
function SortableTH({
  sortKey, activeKey, order, onClick, children, className, align,
}: {
  sortKey: PqSortKey;
  activeKey: PqSortKey;
  order: "asc" | "desc";
  onClick: (k: PqSortKey) => void;
  children: React.ReactNode;
  className?: string;
  align?: "left" | "right";
}) {
  return (
    <TableHead className={cn(align === "right" && "text-right", className)}>
      <SortLink
        sortKey={sortKey}
        activeKey={activeKey}
        order={order}
        onClick={onClick}
      >
        {children}
      </SortLink>
    </TableHead>
  );
}

// The bare button + arrow — usable inline alongside other header content
// (e.g., a popover trigger).
function SortLink({
  sortKey, activeKey, order, onClick, children,
}: {
  sortKey: PqSortKey;
  activeKey: PqSortKey;
  order: "asc" | "desc";
  onClick: (k: PqSortKey) => void;
  children: React.ReactNode;
}) {
  const active = activeKey === sortKey;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      className={cn(
        "inline-flex items-center gap-1 hover:text-foreground",
        active ? "text-foreground" : "text-muted-foreground",
      )}
    >
      {children}
      <span aria-hidden className={cn("text-[10px]", !active && "opacity-30")}>
        {active && order === "asc" ? "↑" : "↓"}
      </span>
    </button>
  );
}
